import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'artego_cookie_consent';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved.preferences || preferences);
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
      }
    }
  }, []);

  const saveConsent = (type) => {
    let finalPreferences = { ...preferences };
    
    if (type === 'all') {
      finalPreferences = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true
      };
    } else if (type === 'necessary') {
      finalPreferences = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false
      };
    }
    // 'custom' uses current preferences state

    const consentData = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      preferences: finalPreferences
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);

    // Here you would typically initialize/disable analytics based on preferences
    if (finalPreferences.analytics) {
      // Initialize analytics (e.g., Google Analytics)
      console.log('Analytics cookies enabled');
    }
    if (finalPreferences.marketing) {
      // Initialize marketing cookies
      console.log('Marketing cookies enabled');
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 bg-black/30 backdrop-blur-sm">
      <Card className="w-full max-w-2xl bg-white shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Cookie className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Informativa sui Cookie
                </h2>
                <p className="text-sm text-stone-500">
                  ARTEGO.APP rispetta la tua privacy
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-stone-600 mb-4 leading-relaxed">
            Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza, 
            analizzare il traffico del sito e personalizzare i contenuti. 
            I cookie necessari sono essenziali per il funzionamento del sito e non possono essere disattivati.
          </p>

          {/* Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-4"
          >
            <Shield className="h-4 w-4" />
            Personalizza preferenze
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {/* Cookie Options */}
          {showDetails && (
            <div className="border rounded-lg p-4 mb-4 space-y-4 bg-stone-50">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="font-medium text-slate-900">Cookie Necessari</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Essenziali per il funzionamento del sito. Non possono essere disattivati.
                  </p>
                </div>
                <Switch checked={true} disabled className="data-[state=checked]:bg-green-600" />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="font-medium text-slate-900">Cookie Analitici</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Ci aiutano a capire come utilizzi il sito per migliorare l'esperienza.
                  </p>
                </div>
                <Switch 
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                />
              </div>

              {/* Preference Cookies */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="font-medium text-slate-900">Cookie di Preferenze</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Ricordano le tue preferenze come lingua e regione.
                  </p>
                </div>
                <Switch 
                  checked={preferences.preferences}
                  onCheckedChange={(checked) => setPreferences({...preferences, preferences: checked})}
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="font-medium text-slate-900">Cookie di Marketing</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Utilizzati per mostrarti pubblicità pertinenti ai tuoi interessi.
                  </p>
                </div>
                <Switch 
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => saveConsent('necessary')}
            >
              Solo Necessari
            </Button>
            {showDetails && (
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => saveConsent('custom')}
              >
                Salva Preferenze
              </Button>
            )}
            <Button 
              className="flex-1 bg-slate-900 hover:bg-slate-800"
              onClick={() => saveConsent('all')}
            >
              Accetta Tutti
            </Button>
          </div>

          {/* Privacy Policy Link */}
          <p className="text-xs text-stone-500 text-center mt-4">
            Per maggiori informazioni, consulta la nostra{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </a>
            {' '}e la{' '}
            <a href="/cookie-policy" className="text-blue-600 hover:underline">
              Cookie Policy
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CookieBanner;
